import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { CardItem } from '../../../components/react/Cards/types';
import { eventBus } from '../../../helpers/resize';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import ListingFilters from './listing-filters';

interface Props {
  endpoint: string;
  itemsPerPageDesktop: string;
  itemsPerPageMobile: string;
  cardVariant: string;
  filterLabel: string;
  filterLabelMobile: string;
  viewDetailsLabel: string;
  bookingLabel: string;
  allFilterLabel: string;
  preSelected?: string;
  propertyFilterId?: string;
  showDescription?: string;
  callToBookLabel?: string;
}

export interface EndpointResponse {
  data: {
    cards: [];
    filters?: Options[];
    pagination?: {
      totalItems: number;
    };
  };
}

export interface Options {
  id: string;
  value: string;
}

const ListingCards = React.lazy(() => import('./listing-cards'));

const OffersListing = ({
  endpoint,
  itemsPerPageDesktop,
  itemsPerPageMobile,
  cardVariant,
  filterLabel,
  filterLabelMobile,
  viewDetailsLabel,
  bookingLabel,
  allFilterLabel,
  propertyFilterId,
  showDescription,
  preSelected,
  callToBookLabel
}: Props) => {
  const FilterAllId = 'all';
  const sarchQueryName = 'listing-location';

  const [page, setPage] = useState(1);
  const [items, setItems] = useState<CardItem[]>([]);
  const [selectedLocations, setSelectedLocations] = useState(
    preSelected ? [preSelected] : [FilterAllId]
  );
  const [itemsPerPage, setItemsPerPage] = useState(0);
  const [filters, setFilters] = useState<Options[] | null>(null);
  const [firstLoad, setFirstLoad] = useState(true);
  const cardVariantLower = cardVariant?.toLowerCase();
  const observer = useRef<IntersectionObserver>();

  const updateItemsPerPage = (breakpoint: string) => {
    if (breakpoint === 'mobile') {
      return setItemsPerPage(parseInt(itemsPerPageMobile));
    }

    return setItemsPerPage(parseInt(itemsPerPageDesktop));
  };

  const subscribeToBreakpointChange = () => {
    eventBus.subscribe('breakpointChange', updateItemsPerPage);
  };

  useEffect(() => {
    // Call the function initially
    const initialBreakpoint = window.innerWidth <= 768 ? 'mobile' : 'desktop';
    updateItemsPerPage(initialBreakpoint);

    // Subscribe to breakpoint changes
    subscribeToBreakpointChange();
  }, []);

  const fetchCards = async ({
    page,
    itemsPerPage,
    selectedLocations
  }: {
    page: number;
    itemsPerPage: number;
    selectedLocations: string[];
  }) => {
    if (page === 0 || itemsPerPage === 0) {
      return;
    }

    let queryParams = `?page=${page}&itemsPerPage=${itemsPerPage}`;

    if (selectedLocations) {
      queryParams += `&location=${selectedLocations}`;
    }

    if (propertyFilterId) {
      queryParams += `&propertyFilterId=${propertyFilterId}`;
    }

    const response = await fetch(`${endpoint}${queryParams}`);

    const data = (await response.json()) as EndpointResponse;

    const filterAll = { id: FilterAllId, value: allFilterLabel };
    data.data.filters?.unshift(filterAll);

    setFilters(data.data.filters ?? []);
    setItems([...items, ...data.data.cards]);

    return data.data;
  };

  const { error, fetchNextPage, isFetching, hasNextPage, isLoading } = useSuspenseInfiniteQuery({
    queryKey: ['cards', itemsPerPage, selectedLocations, page],
    queryFn: ({ pageParam }) => fetchCards({ page: pageParam, itemsPerPage, selectedLocations }),
    initialPageParam: page,
    refetchOnWindowFocus: false,
    getNextPageParam: res => {
      const totalItems = res?.pagination?.totalItems ?? 0;
      const totalPages = Math.ceil(totalItems / itemsPerPage);
      if (itemsPerPage > 0 && page < totalPages) {
        return page + 1;
      }
      return undefined;
    }
  });

  const lastElementRef = useCallback(
    (node: HTMLLIElement) => {
      if (isLoading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && hasNextPage && !isFetching) {
            setPage(page + 1);
            fetchNextPage();
          }
        },
        { threshold: 1, rootMargin: '0px 0px 100px 0px' }
      );

      if (node) observer.current.observe(node);
    },
    [fetchNextPage, hasNextPage, isFetching, isLoading, page]
  );

  const handlerSelectedLocation = (locations: string[]) => {
    // Convert both arrays to Sets for easier comparison
    const newLocationsSet = new Set(locations);
    const currentLocationsSet = new Set(selectedLocations);

    // Check if the new locations are different from the current selected locations
    // by comparing the size of the sets and ensuring every element in one set is present in the other
    const isDifferent =
      newLocationsSet.size !== currentLocationsSet.size ||
      [...newLocationsSet].some(location => !currentLocationsSet.has(location));

    if (!isDifferent) {
      return;
    }

    setPage(1);
    setSelectedLocations(locations);
    updateQueryString(locations);
    setItems([]);
  };

  if (error) return 'An error has occurred: ' + error.message;

  const scrollToSection = () => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const updateQueryString = (queryString: string[]) => {
    const url = new URL(window.location.href);
    url.searchParams.set(sarchQueryName, queryString.join(','));
    window.history.replaceState({}, '', url.toString());

    if (queryString.length === 1 && queryString[0] === FilterAllId) {
      const url = new URL(window.location.href);
      url.searchParams.delete(sarchQueryName);
      window.history.replaceState({}, '', url.toString());
      return;
    }
  };

  useEffect(() => {
    const searchQuery = new URLSearchParams(window.location.search).get(sarchQueryName);
    if (searchQuery) {
      setSelectedLocations(searchQuery.split(','));
    }
  }, []);

  useEffect(() => {
    if (firstLoad && items.length > 0) {
      setFirstLoad(false);

      setTimeout(() => {
        scrollToSection();
      }, 500);
    }
  }, [items, firstLoad]);

  return (
    <Suspense
      fallback={
        <>
          <div className="loading">Loading...</div>
        </>
      }
    >
      <ListingFilters
        filterLabel={filterLabel}
        filterLabelMobile={filterLabelMobile}
        filters={filters ?? []}
        selectedLocations={selectedLocations}
        handlerSelectedLocation={handlerSelectedLocation}
      />

      <ListingCards
        items={items}
        cardVariantLower={cardVariantLower}
        lastElementRef={lastElementRef}
        viewDetailsLabel={viewDetailsLabel}
        bookingLabel={bookingLabel}
        showDescription={showDescription === 'true'}
        callToBookLabel={callToBookLabel}
      />
    </Suspense>
  );
};

export default OffersListing;
